import * as React from "react"
import {
  FaPlay,
  FaStop,
  FaFacebook,
  FaTwitter,
  FaYoutube,
  FaVimeo,
  FaInstagram,
  FaTwitch
} from "react-icons/fa"
import { useRecoilValue, useRecoilState } from "recoil"
import { BsBroadcast } from "react-icons/bs"
import { brandingSelector } from "../../../../recoil/atoms/room"
import ConfirmModal from "../../../common/confirmModal"
import type {
  StreamContainerProps,
  Stream
} from "./stream-container.interfaces"
import { Wrapper, Title, Text, Button } from "../../../styles/common.styled"
import {
  StreamBox,
  StreamIcon,
  StartStreamingButton,
  StreamSavedList,
  StreamForm,
  StreamFacebookIcon,
  StreamTwitterIcon,
  StreamSavedContainer,
  StreamInstagramIcon,
  StreamTwitchIcon,
  StreamVimeoIcon,
  StreamYoutubeIcon,
  StreamBroadcastIcon,
  StreamTextTip
} from "./stream-container.styled"
import { Box, Spinner } from "@chakra-ui/react"
import * as Yup from "yup"

import {
  streamsAtom,
  streamAllStatusAtom,
  streamsLoaderAtom,
  streamsStartAtom,
  connectBotStreamAtom,
  streamFormRTMPURLAtom,
  streamFormLabelAtom,
  streamFormStreamKeyAtom
} from "../../../../recoil/atoms/streams"
import { SDKContext } from "../../../../context/SDKContext"
import { StreamItem } from "./stream-item"
import { CommonInput } from "../../../common/fields"
import useWindowSize from "../../../../hooks/useWindowSize"
import { useTranslation } from "react-i18next"

const networkIconsMap = [
  { keys: ["facebook"], icon: <StreamFacebookIcon as={FaFacebook} /> },
  { keys: ["twitter"], icon: <StreamTwitterIcon as={FaTwitter} /> },
  { keys: ["instagram"], icon: <StreamInstagramIcon as={FaInstagram} /> },
  { keys: ["youtube"], icon: <StreamYoutubeIcon as={FaYoutube} /> },
  { keys: ["twitch", "contribute"], icon: <StreamTwitchIcon as={FaTwitch} /> },
  { keys: ["vimeo"], icon: <StreamVimeoIcon as={FaVimeo} /> }
]

const StreamContainer: React.FC<StreamContainerProps> = ({
  setDisconnectStreamBot
}) => {
  const { t } = useTranslation()
  const meetingClient = React.useContext(SDKContext)

  const { screenSize } = useWindowSize()
  const isSmallWindowHeight = React.useMemo(
    () => 570 > screenSize.height || screenSize.width < 1024,
    [screenSize.height, screenSize.width]
  )

  const [rtmpUrl, setRtmpUrl] = useRecoilState(streamFormRTMPURLAtom)
  const [streamKey, setStreamKey] = useRecoilState(streamFormStreamKeyAtom)
  const [label, setLabel] = useRecoilState(streamFormLabelAtom)

  const [currentStream, setCurrentStream] = React.useState<Stream | null>(null)
  const [errorRTMP, setErrorRTMP] = React.useState<string>("")
  const [errorStreamKey, setErrorStreamKey] = React.useState<string>("")
  const [errorLabel, setErrorLabel] = React.useState<string>("")

  const roomStyles = useRecoilValue(brandingSelector)
  const streamsList = useRecoilValue(streamsAtom)

  const pickUpIcon = (url: string): React.ReactNode => {
    let icon = null
    if (url) {
      icon = networkIconsMap.filter(networkIcon => {
        const result = networkIcon.keys.filter(key => url.includes(key))
        return result.length > 0 ? true : false
      })[0]?.icon
    }
    return icon ?? <StreamBroadcastIcon as={BsBroadcast} />
  }

  const schemaRTMP = Yup.string()
    .test("MustbeRTMPURL", t("Must be RTMP URL"), function () {
      return rtmpUrl.startsWith("rtmps://") || rtmpUrl.startsWith("rtmp://")
    })
    .max(1024, "MaxRTMP")
    .nullable()
    .trim()
  
  const schemaStreamKey = Yup.string()
    .max(1024, "MaxStreamKey")
    .test("nospace", "StreamKeyIsEmpty", function (value) {
      const text = value.split("")
      const haveSpace = element => element !== " "
      return text.some(haveSpace)
    })
    .nullable()
    .trim()

  const schemaLabel = Yup.string()
    .max(1024, "MaxLabel")
    .test("nospace", "LabelIsEmpty", function (value) {
      const text = value.split("")
      const haveSpace = element => element !== " "
      return text.some(haveSpace)
    })
    .nullable()
    .trim()

  const clearInputFields = () => {
    setRtmpUrl("")
    setStreamKey("")
    setLabel("")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    try {
      e.preventDefault()
      // validate the input
      const validRtmpUrl = await schemaRTMP.validate(rtmpUrl)
      setErrorRTMP("") // clear old error after valid
      const validStreamKey = await schemaStreamKey.validate(streamKey)
      setErrorStreamKey("")
      const validLabel = await schemaLabel.validate(label)
      setErrorLabel("")
      
      // concat the rtmp url and stream key
      const separator = validRtmpUrl.endsWith("/") ? "" : "/"
      const fullRtmpURL = validRtmpUrl + separator + validStreamKey
      
      // send it to server
      meetingClient?.sendStreamConfig(validLabel, fullRtmpURL)

      clearInputFields()     
    } catch (e) {
      if (e.message.includes("RTMP URL")) {
        setErrorRTMP("Must be RTMP URL")
      }
      if (e.message.includes("MaxRTMP")) {
        setErrorRTMP("max. 1024 char")
      }

      if (e.message.includes("StreamKeyIsEmpty")) {
        setErrorStreamKey("Must be Stream Key")
      }

      if (e.message.includes("MaxStreamKey")) {
        setErrorStreamKey("max. 1024 char")
      }

      if (e.message.includes("MaxLabel")) {
        setErrorLabel("max. 1024 char")
      }

      if (e.message.includes("LabelIsEmpty")) {
        setErrorLabel("Label is empty")
      }
    }
  }

  const handleDeleteStream = React.useCallback(() => {
    meetingClient?.deleteStreamConfig(currentStream!.id)

    // clear current stream and close confirm modal
    setCurrentStream(null)
  }, [currentStream, meetingClient])

  const handleCloseConfirmModal = React.useCallback(() => {
    // close confirm modal
    setCurrentStream(null)
  }, [])

  const streamStatus = useRecoilValue(streamAllStatusAtom)
  const [isStreamsLoader, setSteamsLoader] =
    useRecoilState<boolean>(streamsLoaderAtom)
  const [isStreamsStart, setSteamsStart] = useRecoilState<boolean>(streamsStartAtom)
  const [, setConnectStreamBot] = useRecoilState<boolean>(connectBotStreamAtom)

  const isStreamAll = React.useMemo(
    () => (streamStatus === "start" ? true : false),
    [streamStatus]
  )

  const handleToggleAllStreams = () => {
    const streams = streamsList.reduce(
      (acc, stream, index, array) =>
        index === array.length - 1
          ? acc + `${stream.id}`
          : acc + `${stream.id},`,
      ""
    )
    setSteamsLoader(true)
    if (isStreamsStart) {
      setConnectStreamBot(false)
      setDisconnectStreamBot(true)
      meetingClient?.toggleAllStreams(streams, "stop")
    } else {
      meetingClient?.toggleAllStreams(streams, "start")
      setSteamsStart(true)
    }
  }


  const InputBlock = React.useCallback(({ title, subTitle, placeholder, onChange, value, error}) => {
    return (
     <>
      <Text
        isSmallWindowHeight={isSmallWindowHeight}
        style={{ marginBottom: 0 }}
        roomStyles={roomStyles}
      >
        { title }
      </Text>
      <StreamTextTip
        isSmallWindowHeight={isSmallWindowHeight}
        error={error}
        roomStyles={roomStyles}
      >
        { subTitle }
      </StreamTextTip>

      <StreamBox>
        <CommonInput
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
          value={value}
          error={error}
          fontSize={isSmallWindowHeight ? "0.9rem" : "1rem"}
          height={isSmallWindowHeight ? "2rem" : "2.5rem"}
        />
      </StreamBox>
     </>
    )
  },[isSmallWindowHeight, roomStyles])

  return (
    <Wrapper
      roomStyles={roomStyles}
      style={{ width: isSmallWindowHeight ? "300xp" : "100%" }}
    >
      <ConfirmModal
        isOpen={!!currentStream}
        onClose={handleCloseConfirmModal}
        onConfirm={handleDeleteStream}
        text={t("Are you sure you want to remove?")}
      />

      {/* Title */}
      <Title
        isSmallWindowHeight={isSmallWindowHeight}
        roomStyles={roomStyles}
        style={{ marginBottom: "0px" }}
      >
        {t("Stream to social media")}
      </Title>
      <StreamTextTip
        isSmallWindowHeight={isSmallWindowHeight}
        error={errorRTMP}
        roomStyles={roomStyles}
        style={{ fontWeight: 500 }}
      >
        {t("Supported on Facebook, YouTube and Twitch")}
      </StreamTextTip>

      <StreamForm onSubmit={handleSubmit}>

        {/* RTMP URL */}
        <InputBlock
          title={t("RTMP URL")}
          subTitle={t(
            "This should be the RTMP URL of where you want to broadcast the main video window of your room to"
          )}
          placeholder="rtmps://..."
          value={rtmpUrl}
          onChange={setRtmpUrl}
          error={errorRTMP}
        />

        {/* Stream key */}
        <InputBlock
          title={t("Stream key")}
          subTitle={t(
            "This is provided by the social network you wish to stream to"
          )}
          placeholder="Key"
          value={streamKey}
          onChange={setStreamKey}
          error={errorStreamKey}
        />

        {/* Stream Label  */}
        <InputBlock
          title={t("Stream label")}
          subTitle={t(
            "This will enable you to easily identify your room stream should you wish to set up multiple streaming endpoints"
          )}
          placeholder="e.g. Facebook"
          value={label}
          onChange={setLabel}
          error={errorLabel}
        />
      
        {/* Save Button */}
        <Button
          isSmallWindowHeight={isSmallWindowHeight}
          roomStyles={roomStyles}
          type="submit"
          disabled={streamsList.length === 5 || label === "" || rtmpUrl === "" || streamKey === ""}
        >
          {t("Save")}
        </Button>
      </StreamForm>
      
      {streamsList.length > 0 && (
        <StreamSavedContainer>
          <Title roomStyles={roomStyles}>{t("Saved streams")}</Title>
          <StreamSavedList>
            {[...streamsList].reverse().map((stream, index) => {
              return (
                <Box key={`${stream.id}-item`}>
                  <StreamItem
                    pickUpIcon={pickUpIcon}
                    isStartStream={isStreamAll}
                    setCurrentStream={setCurrentStream}
                    roomStyles={roomStyles}
                    stream={stream}
                  />
                </Box>
              )
            })}
          </StreamSavedList>
          <StartStreamingButton
            roomStyles={roomStyles}
            style={{ width: "100%", marginLeft: "0px", marginTop: "0.4rem" }}
            type="button"
            onClick={handleToggleAllStreams}
            disabled={isStreamsLoader}
          >
            {isStreamsStart ? (
              <>
                <StreamIcon as={FaStop} />
                <p>
                  {isStreamsLoader
                    ? t("Stopping streaming…")
                    : t("Stop streaming")}
                </p>
                {isStreamsLoader && <Spinner size="sm" marginLeft={"5px"} />}
              </>
            ) : (
              <>
                <StreamIcon as={FaPlay} />
                <p>
                  {
                    isStreamsLoader
                      ? t("Starting streaming…")
                      : streamsList.length > 1
                      ? t("Stream all")
                      : t("Start streaming") // TODO: Add in language file
                  }
                </p>
                {isStreamsLoader && <Spinner size="sm" marginLeft={"5px"} />}
              </>
            )}
          </StartStreamingButton>

          <StreamTextTip
            isSmallWindowHeight={isSmallWindowHeight}
            roomStyles={roomStyles}
          >
            {t("Note: Whenever you stream, a recording will also be made")}
          </StreamTextTip>
          
        </StreamSavedContainer>
      )}
    </Wrapper>
  )
}

export default StreamContainer
